import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import SectionHead from '../shared/SectionHead';
import { useReveal } from '../../hooks/useReveal';
import { CloudGlyph, BrowserGlyph, PhoneGlyph } from './Glyphs';

const NODE_IDS = ['cloud', 'web', 'app'];

function Glyph({ id }) {
  if (id === 'cloud') return <CloudGlyph />;
  if (id === 'web') return <BrowserGlyph />;
  return <PhoneGlyph />;
}

export default function PVCloudFlow() {
  const { t } = useTranslation();
  const ref = useReveal({ stagger: 0.08 });
  const nodes = t('pv.cloud_flow.nodes', { returnObjects: true });

  return (
    <section
      ref={ref}
      className="section"
      style={{ background: 'var(--p2a-dark-panel)', borderBottom: '1px solid var(--p2a-dark-border)' }}
    >
      <div className="p2-container">
        <SectionHead
          eyebrow={t('pv.cloud_flow.eyebrow')}
          title={t('pv.cloud_flow.title')}
          lede={t('pv.cloud_flow.lede')}
          max={620}
        />

        <div
          className="pv-flow"
          style={{
            marginTop: 56,
            display: 'grid',
            gridTemplateColumns: '1fr auto 1fr auto 1fr',
            alignItems: 'stretch',
            gap: 0,
          }}
        >
          {nodes.map((n, i) => (
            <Fragment key={NODE_IDS[i]}>
              <div
                className="reveal"
                style={{
                  border: '1px solid var(--p2a-dark-border-2)',
                  borderRadius: 6,
                  background: 'var(--p2a-dark-bg)',
                  padding: '28px 24px 24px',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
                  <div className="mono" style={{
                    width: 32, height: 32, flexShrink: 0,
                    border: '1px solid var(--p2a-green-500)',
                    color: 'var(--p2a-green-400)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 12, fontWeight: 600, letterSpacing: '0.06em',
                    borderRadius: 4,
                  }}>{String(i + 1).padStart(2, '0')}</div>
                  <span className="mono" style={{ fontSize: 11, letterSpacing: '0.14em', color: 'var(--p2a-dark-fg-4)' }}>
                    {n.tag}
                  </span>
                </div>

                <div style={{
                  height: 120,
                  marginBottom: 20,
                  border: '1px solid var(--p2a-dark-border)',
                  borderRadius: 4,
                  background: 'var(--p2a-dark-panel)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  position: 'relative',
                  overflow: 'hidden',
                }}>
                  <Glyph id={NODE_IDS[i]} />
                </div>

                <h3 className="h-3" style={{ marginBottom: 10 }}>{n.title}</h3>
                <p style={{ fontSize: 14, lineHeight: 1.55, color: 'var(--p2a-dark-fg-3)' }}>{n.desc}</p>

                <ul style={{ listStyle: 'none', padding: 0, margin: '18px 0 0', display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {n.bullets.map((b, bi) => (
                    <li key={bi} style={{ display: 'flex', gap: 10, fontSize: 12.5, color: 'var(--p2a-dark-fg-2)' }}>
                      <span style={{ color: 'var(--p2a-green-500)', flexShrink: 0 }}>→</span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {i < nodes.length - 1 && (
                <div className="pv-flow-arrow reveal" style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  padding: '0 20px',
                  color: 'var(--p2a-green-400)',
                }}>
                  <svg width="48" height="14" viewBox="0 0 48 14" fill="none" aria-hidden="true">
                    <path d="M0 7 L42 7 M36 2 L42 7 L36 12" stroke="currentColor" strokeWidth="1.2" strokeLinecap="square" strokeLinejoin="miter" />
                  </svg>
                </div>
              )}
            </Fragment>
          ))}
        </div>
      </div>

      <style>{`
        @media (max-width: 900px) {
          .pv-flow { grid-template-columns: 1fr !important; gap: 16px !important; }
          .pv-flow-arrow { transform: rotate(90deg); padding: 8px 0 !important; }
        }
      `}</style>
    </section>
  );
}
